import React, { PureComponent, ReactNode } from 'react';

import qs from 'qs';
import { FormattedMessage, injectIntl, InjectedIntl } from 'react-intl';

import Panel from '@atlaskit/panel';
import { DirectoryParentIcon, IconSizes } from '@atlassian/bitkit-icon';

import Announcer from 'src/components/announcer';
import SourceDirLink from 'src/components/source-dir-link';
import SourceFileLink from 'src/components/source-file-link';
import { Repository } from 'src/components/types';
import { SOURCE_DIRECTORY_LOAD, MetricSender } from 'src/metrics';
import { Ref } from 'src/sections/repository/types';
import { safeEncode } from 'src/utils/path';

import { SrcDirMetadataLookup, TreeEntry } from '../types';
import collapseDirectoryPath from '../utils/collapse-directory-path';
import { getFirstReadme } from '../utils/readme';
import { getSourceItemIcon } from '../utils/source-icons';

import DirectoryPath from './directory-path';
import DirectoryListRow from './directory-row';
import messages from './directory.i18n';
import * as styles from './directory.style';
import { Readme } from './readme';
import SubmoduleLink from './submodule-link';

export type DirectoryProps = {
  contents: TreeEntry[];
  intl: InjectedIntl;
  path: string | null | undefined;
  repository: Repository;
  atRef: Ref | null | undefined;
  srcDirMetadata: SrcDirMetadataLookup | null | undefined;
  hasMetadata: boolean;
  maxItemsDisplayed: number;
};

class Directory extends PureComponent<DirectoryProps> {
  static defaultProps = {
    /* eslint react/default-props-match-prop-types: "warn" */
    contents: [],
  };

  renderLinkContent = (icon: ReactNode, text: string) => (
    <styles.LinkInnerWrapper>
      {icon && <styles.LinkIconContainer>{icon}</styles.LinkIconContainer>}
      <styles.LinkTextContainer>{text}</styles.LinkTextContainer>
    </styles.LinkInnerWrapper>
  );

  renderDirectory() {
    const {
      contents,
      intl,
      srcDirMetadata,
      hasMetadata,
      maxItemsDisplayed,
      path,
    } = this.props;
    const filesCount = contents.filter(item => item.type === 'file').length;

    return (
      <>
        <table data-qa="repository-directory">
          <thead>
            <tr>
              <styles.ItemNameHeader colSpan={hasMetadata ? 1 : 4}>
                <FormattedMessage {...messages.columnHeaderName} />
              </styles.ItemNameHeader>
              {hasMetadata && (
                <>
                  <styles.ItemSizeHeader>
                    <FormattedMessage {...messages.columnHeaderSize} />
                  </styles.ItemSizeHeader>
                  <styles.ItemCommitDateHeader>
                    <FormattedMessage
                      {...messages.columnHeaderLastCommitHash}
                    />
                  </styles.ItemCommitDateHeader>
                  <styles.ItemMessageHeader>
                    <FormattedMessage
                      {...messages.columnHeaderLastCommitMessage}
                    />
                  </styles.ItemMessageHeader>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {this.renderParentDirectoryLink()}
            {contents.slice(0, maxItemsDisplayed).map(r => {
              const { atRef } = this.props;
              const extendedRow = {
                ...r,
                icon: getSourceItemIcon(r.type, intl),
              };

              const linkName =
                extendedRow.type === 'directory'
                  ? `${collapseDirectoryPath(extendedRow)
                      .split('/')
                      .map(safeEncode)
                      .join('/')}/`
                  : safeEncode(extendedRow.name);
              const url = atRef
                ? `${linkName}?${qs.stringify({ at: atRef.name })}`
                : linkName;

              const fileMetadata =
                srcDirMetadata && srcDirMetadata[extendedRow.name];

              return (
                <DirectoryListRow
                  hideFileMetadata={!hasMetadata}
                  key={extendedRow.name}
                  size={fileMetadata ? fileMetadata.size : undefined}
                  lastModified={
                    fileMetadata ? fileMetadata.lastModified : undefined
                  }
                  commitLink={
                    fileMetadata && fileMetadata.lastModified
                      ? fileMetadata.lastModified.links.html.href
                      : undefined
                  }
                >
                  {this.directoryRowNameContents(extendedRow, url)}
                </DirectoryListRow>
              );
            })}
          </tbody>
        </table>
        <Announcer
          text={intl.formatMessage(messages.announceStructure, {
            directoriesCount: contents.length - filesCount,
            filesCount,
            path: path || 'root',
          })}
        />
        {contents.length > maxItemsDisplayed && (
          <styles.ListFooter>
            <FormattedMessage
              {...messages.tooManyItemsToDisplay}
              values={{ count: maxItemsDisplayed }}
            />
          </styles.ListFooter>
        )}
      </>
    );
  }

  renderParentDirectoryLink = () => {
    const { intl, path } = this.props;

    if (!path) {
      return null;
    }

    return (
      <DirectoryListRow>
        <SourceDirLink
          href=".."
          aria-label={intl.formatMessage(messages.parentDirectoryIconLabel)}
        >
          {this.renderLinkContent(
            <DirectoryParentIcon size={IconSizes.Medium} label="" />,
            '..'
          )}
        </SourceDirLink>
      </DirectoryListRow>
    );
  };

  // @ts-ignore TODO: fix noImplicitAny error here
  directoryRowNameContents = (entry, url) => {
    if (entry.type === 'submodule') {
      return <SubmoduleLink entry={entry} icon={entry.icon} />;
    }

    if (entry.type === 'directory') {
      return (
        <SourceDirLink href={url}>
          {this.renderLinkContent(entry.icon, collapseDirectoryPath(entry))}
        </SourceDirLink>
      );
    }

    return (
      <SourceFileLink to={url}>
        {this.renderLinkContent(entry.icon, entry.name)}
      </SourceFileLink>
    );
  };

  render() {
    const { contents, path, repository } = this.props;

    const readme = getFirstReadme(contents);

    const header = (
      <DirectoryPath
        repositorySlug={repository.full_name.split('/')[1]}
        path={path}
      />
    );

    if (readme) {
      return (
        <>
          <Panel header={header} isDefaultExpanded>
            <styles.DirectoryList>
              {this.renderDirectory()}
            </styles.DirectoryList>
          </Panel>
          <Readme path={path ? `${path}/${readme.name}` : readme.name} />
          <MetricSender metric={SOURCE_DIRECTORY_LOAD} />
        </>
      );
    }

    return (
      <>
        <styles.DirectoryList>
          <styles.DirectoryHeader>{header}</styles.DirectoryHeader>
          {this.renderDirectory()}
        </styles.DirectoryList>
        <MetricSender metric={SOURCE_DIRECTORY_LOAD} />
      </>
    );
  }
}

export default injectIntl(Directory);
